import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { formatDateTime } from "@/lib/format";
import { Settings, Loader2, Pencil, Users, SlidersHorizontal, History, Landmark, Download, DatabaseBackup, Check, X, Minus } from "lucide-react";

const OVERRIDE_SHORT = [
  { key: "staff_edit_achievements", short: "Revenue" },
  { key: "staff_create_reports", short: "Report" },
  { key: "staff_create_tasks", short: "Task" },
  { key: "staff_update_profile", short: "Profil" },
  { key: "staff_view_channels", short: "Channels" },
  { key: "staff_rate_simulator", short: "RateSim" },
  { key: "staff_reservation_report", short: "Resv" },
];

const ENTITIES = ["all", "auth", "staff", "property", "target", "achievement", "finance", "bonus", "task", "report", "leave", "channel", "settings", "shift", "schedule"];

const FEATURES = [
  { key: "staff_edit_achievements", label: "Edit weekly revenue", desc: "Staff can fill in weekly revenue figures on the Performance grid." },
  { key: "staff_create_reports", label: "Create reports", desc: "Staff can submit work reports with photo proof." },
  { key: "staff_create_tasks", label: "Create tasks", desc: "Staff can create Kanban tasks for properties they are assigned to." },
  { key: "staff_update_profile", label: "Update own profile & password", desc: "Staff can change their display name and login password via My Account." },
  { key: "staff_view_channels", label: "View OTA & Channels", desc: "Staff can open the OTA & Channels vault (read-only) to see channel credentials." },
  { key: "staff_rate_simulator", label: "Rate Simulator", desc: "Staff can open the Rate Parity Simulator." },
  { key: "staff_reservation_report", label: "Reservation Report", desc: "Staff can input reservation entries and view the Daily/Monthly reservation report." },
];

function EditStaffDialog({ s, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: s.name, email: s.email, password: "" });
  }, [open, s]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password) payload.password = form.password;
      await api.patch(`/admin/staff/${s.id}`, payload);
      toast.success("Staff account updated");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-500 hover:text-indigo-400" title="Edit account" data-testid={`edit-staff-${s.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit staff — {s.name}</DialogTitle>
          <DialogDescription>Update account details or reset the login password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="edit-staff-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Login email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="edit-staff-email" />
          </div>
          <div className="space-y-1.5">
            <Label>New password (leave blank to keep current)</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" data-testid="edit-staff-password" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="edit-staff-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BackupCard() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/settings/backup?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-bedsolvingops-${month}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup ${month} berhasil diunduh`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="backup-card">
      <div className="flex items-center gap-2 mb-1">
        <DatabaseBackup className="h-4 w-4 text-indigo-400" />
        <h2 className="font-semibold text-white">Backup Data</h2>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Download seluruh data aplikasi sebagai JSON. Data bulanan (attendance, leave, reports, audit log, push integrasi) difilter per bulan terpilih;
        data master (staff, property, channel, partner, task, schedule, target) selalu lengkap. File upload (foto/dokumen) tetap di object storage — backup berisi path-nya saja.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-slate-800 border-slate-700 text-slate-200" data-testid="backup-month-input" />
        <Button onClick={download} disabled={downloading} data-testid="backup-download-btn">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download backup
        </Button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [staff, setStaff] = useState([]);
  const [flags, setFlags] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [entity, setEntity] = useState("all");
  const [logsLoading, setLogsLoading] = useState(true);
  const [integ, setInteg] = useState({ webhook_url: "", api_key: "", has_api_key: false, api_key_masked: "" });
  const [savingInteg, setSavingInteg] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, f, i, o] = await Promise.all([
        api.get("/admin/staff"),
        api.get("/settings/feature-access"),
        api.get("/admin/integrations/accounting"),
        api.get("/admin/settings/feature-overrides"),
      ]);
      setStaff(s.data.staff);
      setFlags(f.data.flags);
      setOverrides(o.data.overrides || {});
      setInteg((prev) => ({ ...prev, webhook_url: i.data.webhook_url || "", has_api_key: i.data.has_api_key, api_key_masked: i.data.api_key_masked || "" }));
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get(entity === "all" ? "/admin/audit-logs" : `/admin/audit-logs?entity=${entity}`);
      setLogs(data.logs);
    } catch { toast.error("Failed to load history log"); }
    finally { setLogsLoading(false); }
  }, [entity]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const toggle = async (key, value) => {
    const next = { ...flags, [key]: value };
    setFlags(next);
    try {
      const { data } = await api.put("/admin/settings/feature-access", next);
      setFlags(data.flags);
      toast.success("Feature access updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  const cycleOverride = async (staffId, key) => {
    const cur = (overrides[staffId] || {})[key];
    const next = cur === undefined ? true : cur === true ? false : undefined;
    try {
      const { data } = await api.put(`/admin/settings/feature-overrides/${staffId}`, { flags: { [key]: next === undefined ? null : next } });
      setOverrides(data.overrides);
      toast.success(next === undefined ? "Kembali mengikuti default global" : next ? "Akses diizinkan khusus staff ini" : "Akses diblokir khusus staff ini");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setAllOverrides = async (staffId, mode) => {
    // mode: "reset" (ikut global) | "allow" | "block" — applies to every feature flag at once
    const flags = Object.fromEntries(OVERRIDE_SHORT.map((c) => [c.key, mode === "reset" ? null : mode === "allow"]));
    try {
      const { data } = await api.put(`/admin/settings/feature-overrides/${staffId}`, { flags });
      setOverrides(data.overrides);
      toast.success(mode === "reset" ? "Semua akses kembali ikut default global" : mode === "allow" ? "Semua akses diizinkan" : "Semua akses diblokir");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading || !flags) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1F1B16]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="settings-page">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-white">
            <Settings className="h-6 w-6 text-indigo-500" /> Settings
          </h1>
          <p className="text-sm text-slate-400">Manage staff accounts and control which features staff can edit or create.</p>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="feature-access-card">
          <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-1">
            <SlidersHorizontal className="h-4 w-4 text-amber-400" /> Staff feature access
          </h3>
          <p className="text-xs text-slate-400 mb-2">Toggles apply to all staff accounts immediately.</p>
          {FEATURES.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4 py-3 border-b border-slate-800 last:border-0">
              <div>
                <div className="text-sm font-medium text-slate-100">{f.label}</div>
                <div className="text-xs text-slate-400">{f.desc}</div>
              </div>
              <Switch checked={!!flags[f.key]} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`flag-${f.key}`} />
            </div>
          ))}
        </div>

        <BackupCard />

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="staff-override-card">
          <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-teal-400" /> Per-staff overrides
          </h3>
          <p className="text-xs text-slate-400 mb-4">
            Klik sel untuk mengatur akses per staff: <span className="text-slate-500">—</span> ikut default global, <span className="text-emerald-400">✓</span> izinkan, <span className="text-rose-400">✕</span> blokir.
          </p>
          {staff.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No staff accounts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="staff-override-table">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400">
                    <th className="p-2 font-medium">Staff</th>
                    {OVERRIDE_SHORT.map((c) => (
                      <th key={c.key} className="p-2 font-medium text-center text-[10px]" title={c.key}>{c.short}</th>
                    ))}
                    <th className="p-2 font-medium text-center text-[10px]">Semua</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} className="border-b border-slate-800/60" data-testid={`override-row-${s.id}`}>
                      <td className="p-2 font-medium text-slate-100 whitespace-nowrap">{s.name}</td>
                      {OVERRIDE_SHORT.map((c) => {
                        const v = (overrides[s.id] || {})[c.key];
                        return (
                          <td key={c.key} className="p-1.5 text-center">
                            <button
                              onClick={() => cycleOverride(s.id, c.key)}
                              className={`h-7 w-7 rounded-md inline-flex items-center justify-center border transition-colors ${
                                v === undefined
                                  ? "border-slate-700 text-slate-600 hover:text-slate-300"
                                  : v
                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                                    : "border-rose-500/40 bg-rose-500/10 text-rose-400"
                              }`}
                              data-testid={`override-${s.id}-${c.key}`}
                              title={`${s.name} — ${c.short}: ${v === undefined ? "ikut global" : v ? "izinkan" : "blokir"}`}
                            >
                              {v === undefined ? <Minus className="h-3.5 w-3.5" /> : v ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                        );
                      })}
                      <td className="p-1.5 text-center">
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) setAllOverrides(s.id, e.target.value); e.target.value = ""; }}
                          className="h-7 rounded-md border border-slate-700 bg-slate-800 px-1 text-[11px] text-slate-300"
                          data-testid={`override-all-${s.id}`}
                          title={`Atur semua akses sekaligus untuk ${s.name}`}
                        >
                          <option value="">Semua…</option>
                          <option value="allow">Izinkan semua</option>
                          <option value="block">Blokir semua</option>
                          <option value="reset">Ikut global</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="staff-accounts-card">
          <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-emerald-400" /> Staff accounts
          </h3>
          <p className="text-xs text-slate-400 mb-4">Edit login details or reset a staff password.</p>
          {staff.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No staff accounts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="staff-accounts-table">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400">
                    <th className="p-2.5 font-medium">Name</th>
                    <th className="p-2.5 font-medium">Login email</th>
                    <th className="p-2.5 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`staff-account-${s.id}`}>
                      <td className="p-2.5 font-medium text-slate-100">{s.name}</td>
                      <td className="p-2.5 text-slate-400">{s.email}</td>
                      <td className="p-2.5 text-right"><EditStaffDialog s={s} onSaved={load} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="accounting-integration-card">
          <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-1">
            <Landmark className="h-4 w-4 text-amber-400" /> Accounting integration
          </h3>
          <p className="text-xs text-slate-400 mb-4">Kirim Net Profit bulanan ke Rev.Bedsolving.id via webhook (kategori: Marketing fee).</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Webhook URL</Label>
              <Input
                value={integ.webhook_url}
                onChange={(e) => setInteg({ ...integ, webhook_url: e.target.value })}
                placeholder="https://rev.bedsolving.id/api/integrations/ops/monthly"
                className="bg-slate-800 border-slate-700 text-slate-200"
                data-testid="integration-url-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">API key {integ.has_api_key && <span className="text-xs text-emerald-400 ml-1">(tersimpan: {integ.api_key_masked})</span>}</Label>
              <Input
                type="password"
                value={integ.api_key}
                onChange={(e) => setInteg({ ...integ, api_key: e.target.value })}
                placeholder={integ.has_api_key ? "Kosongkan untuk tetap pakai key tersimpan" : "bsi__…"}
                className="bg-slate-800 border-slate-700 text-slate-200"
                data-testid="integration-key-input"
              />
            </div>
            <Button
              onClick={async () => {
                setSavingInteg(true);
                try {
                  await api.put("/admin/integrations/accounting", { webhook_url: integ.webhook_url, api_key: integ.api_key || null });
                  toast.success("Integration saved");
                  setInteg((p) => ({ ...p, api_key: "" }));
                  load();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                finally { setSavingInteg(false); }
              }}
              disabled={savingInteg || !integ.webhook_url}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="integration-save-btn"
            >
              {savingInteg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save integration"}
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="history-log-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-1">
                <History className="h-4 w-4 text-rose-400" /> History log
              </h3>
              <p className="text-xs text-slate-400">Who accessed or changed what, and when.</p>
            </div>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-slate-200" data-testid="history-entity-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {logsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No activity recorded yet.</p>
          ) : (
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400">
                    <th className="p-2.5 font-medium">Time</th>
                    <th className="p-2.5 font-medium">User</th>
                    <th className="p-2.5 font-medium">Action</th>
                    <th className="p-2.5 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-slate-800/60" data-testid={`history-row-${l.id}`}>
                      <td className="p-2.5 text-xs text-slate-400 whitespace-nowrap">{formatDateTime(l.ts)}</td>
                      <td className="p-2.5">
                        <div className="text-slate-100 text-sm">{l.user_name}</div>
                        <div className="text-[11px] text-slate-500">{l.user_email}</div>
                      </td>
                      <td className="p-2.5"><span className="px-2 py-0.5 rounded-full bg-slate-800 text-xs font-mono text-indigo-300 whitespace-nowrap">{l.action}</span></td>
                      <td className="p-2.5 text-xs text-slate-400 max-w-[280px] truncate" title={l.detail}>{l.detail || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
